import { Megaphone, X } from "lucide-react";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Drawer, DrawerContent, DrawerTitle, DrawerDescription, DrawerClose } from "@/components/ui/drawer";
import { CATEGORY_MAP, imgSrc } from "@/lib/categories";
import { ScheduleList, InfoRows, DetailActions, SourceNote } from "@/components/ListingDetailParts";
import { ReservationBadge, VerifiedBadge } from "@/components/ListingCard";
import { AddToTrip } from "@/components/AddToTrip";
import { useEffect, useState } from "react";
import { track } from "@/lib/track";
import { Reviews } from "@/components/Reviews";
import { DealBox } from "@/components/DealBox";
import { HeartButton } from "@/components/HeartButton";

const MOBILE_Q = "(max-width: 767px)";

function useIsMobile() {
  const [mobile, setMobile] = useState(() => window.matchMedia(MOBILE_Q).matches);
  useEffect(() => {
    const mq = window.matchMedia(MOBILE_Q);
    const f = () => setMobile(mq.matches);
    mq.addEventListener("change", f);
    return () => mq.removeEventListener("change", f);
  }, []);
  return mobile;
}

function DetailBody({ item, city, Title, Description }) {
  const cat = CATEGORY_MAP[item.category] || CATEGORY_MAP.shows;
  return (
    <>
      <div className="relative h-48">
        <img src={imgSrc(item.image_url) || cat.img} alt={item.name} className="h-full w-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-[#151C2C] to-transparent" />
        <HeartButton item={item} className="absolute top-4 left-4 h-10 w-10" />
      </div>
      <div className="px-5 sm:px-6 pb-6 -mt-8 relative space-y-5">
        <div className="text-left space-y-2">
          <span className="text-xs uppercase tracking-wider text-[#FF3B6B] font-semibold">{cat.label}{item.featured ? " · Featured partner" : ""}</span>
          <Title className="text-2xl font-extrabold tracking-tight">{item.name}</Title>
          <Description className="text-sm text-slate-400">{item.description}</Description>
        </div>
        <DealBox item={item} />
        {item.daily_update && (
          <div className="rounded-xl bg-[#FFB800]/10 border border-[#FFB800]/30 p-3 text-sm text-[#FFD466] flex gap-2">
            <Megaphone className="h-4 w-4 mt-0.5 shrink-0" /> {item.daily_update}
          </div>
        )}
        <div className="flex items-center gap-2 flex-wrap">
          <ReservationBadge item={item} />
          <VerifiedBadge item={item} />
          {item.reservation_note && <span className="text-xs text-slate-400">{item.reservation_note}</span>}
        </div>
        <ScheduleList item={item} city={city} />
        <InfoRows item={item} />
        <DetailActions item={item} />
        {city && <AddToTrip item={item} city={city} />}
        <Reviews item={item} city={city} />
        <SourceNote item={item} />
      </div>
    </>
  );
}

export function ListingDetail({ item, onClose, city }) {
  const mobile = useIsMobile();
  useEffect(() => { if (item) track(item, "view"); }, [item]);
  if (!item) return null;
  const onOpenChange = (o) => !o && onClose();

  if (mobile) {
    return (
      <Drawer open={!!item} onOpenChange={onOpenChange} shouldScaleBackground={false}>
        <DrawerContent data-testid={`listing-detail-modal-${item.id}`} className="bg-[#151C2C] border-[#26334D] text-white max-h-[94dvh] rounded-t-3xl outline-none">
          <DrawerClose data-testid="listing-detail-close" aria-label="Close" className="absolute right-3 top-3 z-10 h-9 w-9 grid place-items-center rounded-full bg-[#0B0F19]/70 backdrop-blur text-white">
            <X className="h-4 w-4" />
          </DrawerClose>
          <div data-testid="listing-detail-sheet" className="mt-3 overflow-y-auto overscroll-contain pb-[env(safe-area-inset-bottom)]">
            <DetailBody item={item} city={city} Title={DrawerTitle} Description={DrawerDescription} />
          </div>
        </DrawerContent>
      </Drawer>
    );
  }

  return (
    <Dialog open={!!item} onOpenChange={onOpenChange}>
      <DialogContent data-testid={`listing-detail-modal-${item.id}`}
        className="bg-[#151C2C] border-[#26334D] text-white max-w-lg p-0 overflow-hidden max-h-[92vh] overflow-y-auto">
        <DetailBody item={item} city={city} Title={DialogTitle} Description={DialogDescription} />
      </DialogContent>
    </Dialog>
  );
}
